import { useEffect, useState } from "react";
import { Button, Empty, Spin, Tooltip } from "antd";
import { CheckOutlined, FileAddOutlined } from "@ant-design/icons";
import { makeRequest } from "@utils/helpers";
import { getEmployeeRelatedDataReq } from "@mods/leaveManagement/leave/companyLeaves/requests";

// Dropdown content for a single roster cell: the shift actions, every time slot
// (pick one to assign it to the cell), the employee's leave types, and an
// "Apply Leave" button pinned to the bottom while the lists scroll.
const ShiftCellMenu = ({
  employmentId,
  shift,
  actions,
  timeSlots,
  selectedTimeSlotId,
  canAssignTimeSlot,
  canApplyLeave,
  onSelectTimeSlot,
  onApplyLeave,
}) => {
  // Highlight the staged slot when the cell has an unsaved pick, else the saved one.
  const activeSlotId = selectedTimeSlotId !== undefined ? selectedTimeSlotId : shift?.time_slot_id;
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveLoader, setLeaveLoader] = useState(false);
  // leave is applied against the cell's shift, so a day off has nothing to apply on
  const leaveDisabled = !shift || !canApplyLeave || !!shift.leave;

  useEffect(() => {
    if (leaveDisabled) {
      return;
    }
    makeRequest(setLeaveLoader, getEmployeeRelatedDataReq, { employment_id: employmentId },
      (data, response) => setLeaveTypes(response.leave_types || []), () => setLeaveTypes([]));
  }, [employmentId, leaveDisabled]);

  const leaveDisabledReason = !shift
    ? 'Assign a time slot before applying leave'
    : shift.leave ? 'A leave is already applied on this shift' : 'You are not allowed to apply leave';

  return (
    <div className="shift-cell-menu">
      <div className="shift-cell-menu__scroll">
        <div className="shift-cell-menu__actions">{ actions }</div>

        <div className="shift-cell-menu__heading">Time Slots</div>
        { timeSlots.length ? timeSlots.map((slot) => {
          const isCurrent = activeSlotId === slot.value;
          return (
            <Button key={ slot.value } type="text" block
              className={ `shift-cell-menu__item${isCurrent ? ' shift-cell-menu__item--current' : ''}` }
              icon={ isCurrent ? <CheckOutlined /> : null }
              disabled={ !canAssignTimeSlot }
              onClick={ () => onSelectTimeSlot(slot.value) }
            >
              <span className="shift-cell-menu__label">{ slot.label }</span>
            </Button>
          );
        }) : <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No time slots" /> }

        { !leaveDisabled &&
          <>
            <div className="shift-cell-menu__heading">Leave Types</div>
            <Spin spinning={ leaveLoader } size="small">
              { leaveTypes.map((type) => (
                <Button key={ type.id } type="text" block className="shift-cell-menu__item"
                  onClick={ () => onApplyLeave(type.id) }
                >
                  <span className="shift-cell-menu__label">
                    <strong>{ type.code }</strong>{ ` - ${type.name}` }
                  </span>
                  { type.available !== undefined && type.available !== null &&
                    <span className="shift-cell-menu__meta">{ type.available }</span> }
                </Button>
              )) }
              { !leaveLoader && !leaveTypes.length &&
                <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No leave types assigned" /> }
            </Spin>
          </>
        }
      </div>

      <div className="shift-cell-menu__footer">
        <Tooltip title={ leaveDisabled ? leaveDisabledReason : '' }>
          <Button type="primary" block icon={ <FileAddOutlined /> } disabled={ leaveDisabled } onClick={ () => onApplyLeave(null) }>
            Apply Leave
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default ShiftCellMenu;
